// Reports — aggregation queries over transactions.
// Client-side aggregation: data volume per event is manageable for MVP.
// For production with many transactions, consider Cloud Functions aggregation.

import { supabase } from '@/lib/supabase';
import { loadJsPdf } from '@/lib/lazyChunk';
import { TX_SELECT, mapTransaction } from './mappers';
import type { AppTransaction, EventStatus, PaymentMethod } from '@/types/domain';
import { loadXlsx } from '@/lib/spreadsheet';

/**
 * A returned or cancelled invoice keeps a non-zero outstanding_amount:
 * `tx_paid_plus_outstanding` pins paid + outstanding to grand_total, so
 * create_return() cannot zero it without rewriting the invoice. Nothing owes
 * money on such a row, so every receivable figure has to skip it.
 *
 * 'partially_returned' is deliberately absent — the customer kept some of the
 * goods and still owes for them.
 */
function isReversed(paymentStatus: string | undefined): boolean {
  return paymentStatus === 'returned' || paymentStatus === 'cancelled';
}

/** Sisa piutang as it should be reported: zero once the sale is reversed. */
function reportableOutstanding(t: {
  paymentTerms?: string;
  paymentStatus?: string;
  outstandingAmount?: number;
}): number {
  if (t.paymentTerms !== 'tempo' || isReversed(t.paymentStatus)) return 0;
  return t.outstandingAmount ?? 0;
}

export async function getEventTransactions(eventId: string): Promise<AppTransaction[]> {
  if (!eventId) return [];
  const { data, error } = await supabase
    .from('transactions_view')
    .select(TX_SELECT)
    .eq('event_id', eventId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapTransaction(r as never));
}

export interface PaymentBreakdown {
  method: PaymentMethod;
  count: number;
  total: number;
}

export interface TopProduct {
  productId: string;
  name: string;
  qty: number;
  revenue: number;
}

export interface CashierBreakdown {
  cashierId: string;
  cashierName: string;
  count: number;
  total: number;
}

export interface EventSummary {
  eventId: string;
  totalTransactions: number;
  totalSales: number;
  totalDiscount: number;
  totalTax: number;
  /** Units sold, already NET of returned units (see `returnedItems`). */
  totalItems: number;
  averageTransactionValue: number;
  paymentMethodBreakdown: PaymentBreakdown[];
  topProducts: TopProduct[];
  cashierBreakdown: CashierBreakdown[];
  /** Number of return INVOICES — not units. Pair it with returnsTotal (money). */
  returnsCount: number;
  returnsTotal: number;
  /**
   * Units returned across all transactions. This is what `totalItems` has
   * already been reduced by, so never subtract it from `totalItems` again —
   * it exists to explain that figure, not to adjust it.
   */
  returnedItems: number;
  // ── Receivables (piutang) — Phase 6 ──
  /** Sum of outstandingAmount across all tempo transactions in this event. */
  outstandingTotal: number;
  /** Count of transactions currently overdue (dueDate < now && outstanding > 0). */
  overdueCount: number;
  /** Count of tempo transactions still within their due date. */
  pendingPaymentCount: number;
  /** Count of transactions paid in full (lunas path). */
  lunasCount: number;
  /** Count of tempo transactions (lunasCount + pendingPaymentCount + overdueCount
   *  is NOT necessarily totalTransactions — returned/cancelled are excluded). */
  tempoCount: number;
}

export async function getEventSummary(
  eventId: string,
  opts?: { excludeStatuses?: string[] },
): Promise<EventSummary> {
  const exclude = new Set(opts?.excludeStatuses ?? ['cancelled']);
  const transactions = await getEventTransactions(eventId);
  const filtered = transactions.filter((t) => !exclude.has(t.paymentStatus));

  // Base aggregates
  let totalSales = 0;
  let totalDiscount = 0;
  let totalTax = 0;
  let totalItems = 0;
  let returnedItems = 0;
  let outstandingTotal = 0;
  let overdueCount = 0;
  let pendingPaymentCount = 0;
  let lunasCount = 0;
  let tempoCount = 0;
  const now = Date.now();
  const paymentMap = new Map<PaymentMethod, PaymentBreakdown>();
  const productMap = new Map<string, TopProduct>();
  const cashierMap = new Map<string, CashierBreakdown>();

  for (const tx of filtered) {
    totalSales += tx.grandTotal;
    totalDiscount += tx.discountAmount;
    totalTax += tx.taxAmount;
    for (const it of tx.items) {
      const net = it.qty - (it.returnedQty ?? 0);
      totalItems += net;
      returnedItems += it.returnedQty ?? 0;
      const existing = productMap.get(it.productId);
      if (existing) {
        existing.qty += net;
        existing.revenue += net * it.price;
      } else {
        productMap.set(it.productId, {
          productId: it.productId,
          name: it.name,
          qty: net,
          revenue: net * it.price,
        });
      }
    }
    const p = paymentMap.get(tx.paymentMethod) ?? {
      method: tx.paymentMethod,
      count: 0,
      total: 0,
    };
    p.count += 1;
    p.total += tx.grandTotal;
    paymentMap.set(tx.paymentMethod, p);

    const c = cashierMap.get(tx.cashierId) ?? {
      cashierId: tx.cashierId,
      cashierName: tx.cashierName ?? 'Unknown',
      count: 0,
      total: 0,
    };
    c.count += 1;
    c.total += tx.grandTotal;
    cashierMap.set(tx.cashierId, c);

    // Receivables aggregates. tempoCount stays a count of credit sales made;
    // only the money and the ageing buckets skip reversed invoices.
    if (tx.paymentTerms === 'tempo') {
      tempoCount += 1;
      if (!isReversed(tx.paymentStatus)) {
        outstandingTotal += tx.outstandingAmount;
        if (tx.outstandingAmount > 0) {
          if (tx.dueDate !== undefined && tx.dueDate < now) {
            overdueCount += 1;
          } else {
            pendingPaymentCount += 1;
          }
        }
      }
    } else {
      lunasCount += 1;
    }
  }

  // Returns
  const { data: returnRows, error: retErr } = await supabase
    .from('returns')
    .select('refund_amount')
    .eq('event_id', eventId);
  if (retErr) throw new Error(retErr.message);
  const returnsCount = returnRows?.length ?? 0;
  const returnsTotal = (returnRows ?? []).reduce((sum, r) => sum + (r.refund_amount ?? 0), 0);

  const topProducts = Array.from(productMap.values())
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);
  const paymentMethodBreakdown = Array.from(paymentMap.values()).sort(
    (a, b) => b.total - a.total,
  );
  const cashierBreakdown = Array.from(cashierMap.values()).sort(
    (a, b) => b.total - a.total,
  );

  return {
    eventId,
    totalTransactions: filtered.length,
    totalSales,
    totalDiscount,
    totalTax,
    totalItems,
    averageTransactionValue: filtered.length > 0 ? totalSales / filtered.length : 0,
    paymentMethodBreakdown,
    topProducts,
    cashierBreakdown,
    returnsCount,
    returnsTotal,
    returnedItems,
    outstandingTotal,
    overdueCount,
    pendingPaymentCount,
    lunasCount,
    tempoCount,
  };
}

export interface EventListItem {
  id: string;
  name: string;
  code: string;
  status: EventStatus;
  startAt: number;
  endAt?: number;
  totalSales: number;
  totalTransactions: number;
}

export async function listEventsForReport(): Promise<EventListItem[]> {
  const { data, error } = await supabase
    .from('events').select('*').order('start_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((e) => ({
    id: e.id,
    name: e.name,
    code: e.code,
    status: e.status,
    startAt: new Date(e.start_at).getTime(),
    endAt: e.end_at ? new Date(e.end_at).getTime() : undefined,
    totalSales: e.total_sales,
    totalTransactions: e.total_transactions,
  } satisfies EventListItem));
}

function escapeCsv(value: string | number): string {
  const s = String(value);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function buildEventReportCsv(
  event: { name: string; code: string },
  summary: EventSummary,
): string {
  const lines: string[] = [];
  lines.push('OPHTH POS — Event Report');
  lines.push(`Event,${escapeCsv(event.name)} (${escapeCsv(event.code)})`);
  lines.push(`Generated,${new Date().toISOString()}`);
  lines.push('');
  lines.push('Summary');
  lines.push('Metric,Value');
  lines.push(`Total transactions,${summary.totalTransactions}`);
  lines.push(`Total sales,${summary.totalSales}`);
  lines.push(`Total discount,${summary.totalDiscount}`);
  lines.push(`Total tax,${summary.totalTax}`);
  lines.push(`Total items sold,${summary.totalItems}`);
  lines.push(`Average transaction value,${summary.averageTransactionValue.toFixed(2)}`);
  lines.push(`Returns count,${summary.returnsCount}`);
  lines.push(`Returns total,${summary.returnsTotal}`);
  lines.push(`Lunas count,${summary.lunasCount}`);
  lines.push(`Tempo count,${summary.tempoCount}`);
  lines.push(`Pending payment count,${summary.pendingPaymentCount}`);
  lines.push(`Overdue count,${summary.overdueCount}`);
  lines.push(`Outstanding total,${summary.outstandingTotal}`);
  lines.push('');

  lines.push('Payment Method Breakdown');
  lines.push('Method,Count,Total');
  for (const p of summary.paymentMethodBreakdown) {
    lines.push(`${p.method},${p.count},${p.total}`);
  }
  lines.push('');

  lines.push('Top Products');
  lines.push('Product,Qty,Revenue');
  for (const p of summary.topProducts) {
    lines.push(`${escapeCsv(p.name)},${p.qty},${p.revenue}`);
  }
  lines.push('');

  lines.push('Cashier Breakdown');
  lines.push('Cashier,Transactions,Total');
  for (const c of summary.cashierBreakdown) {
    lines.push(`${escapeCsv(c.cashierName)},${c.count},${c.total}`);
  }
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// XLSX export
// ---------------------------------------------------------------------------
// Multi-sheet Excel workbook: "Summary", "Payment Breakdown", "Top Products",
// "Cashiers", and "Transactions" (full per-transaction list). Generated
// via the `xlsx` (SheetJS) library, lazy-loaded so the ~400KB dependency
// doesn't bloat the main bundle.

interface EventReportEventMeta {
  name: string;
  code: string;
  startAt?: number;
  endAt?: number;
}

export async function buildEventReportXlsx(
  event: EventReportEventMeta,
  summary: EventSummary,
  transactions: PdfTransaction[] = [],
): Promise<Blob> {
  // Dynamic import — Vite splits this into its own chunk.
  const XLSX = await loadXlsx();

  // Sheet 1: Summary
  const summaryRows: (string | number)[][] = [
    ['Metric', 'Value'],
    ['Event', `${event.name} (${event.code})`],
    [
      'Period',
      event.startAt
        ? `${new Date(event.startAt).toISOString()}${
            event.endAt ? ` → ${new Date(event.endAt).toISOString()}` : ''
          }`
        : '—',
    ],
    ['Generated', new Date().toISOString()],
    [],
    ['Total transactions', summary.totalTransactions],
    ['Total sales (IDR)', summary.totalSales],
    ['Total discount (IDR)', summary.totalDiscount],
    ['Total tax (IDR)', summary.totalTax],
    ['Total items sold', summary.totalItems],
    [
      'Average transaction value (IDR)',
      Number(summary.averageTransactionValue.toFixed(2)),
    ],
    ['Returns count', summary.returnsCount],
    ['Returns total (IDR)', summary.returnsTotal],
    ['Lunas count', summary.lunasCount],
    ['Tempo count', summary.tempoCount],
    ['Pending payment count', summary.pendingPaymentCount],
    ['Overdue count', summary.overdueCount],
    ['Outstanding total (IDR)', summary.outstandingTotal],
  ];

  // Sheet 2: Payment method breakdown
  const paymentRows: (string | number)[][] = [
    ['Method', 'Count', 'Total (IDR)'],
    ...summary.paymentMethodBreakdown.map((p) => [p.method, p.count, p.total]),
  ];

  // Sheet 3: Top products
  const productsRows: (string | number)[][] = [
    ['Product', 'Qty', 'Revenue (IDR)'],
    ...summary.topProducts.map((p) => [p.name, p.qty, p.revenue]),
  ];

  // Sheet 4: Cashier breakdown
  const cashiersRows: (string | number)[][] = [
    ['Cashier', 'Transactions', 'Total (IDR)'],
    ...summary.cashierBreakdown.map((c) => [c.cashierName, c.count, c.total]),
  ];

  // Sheet 5: Full transaction list (only if there are transactions)
  const txRows: (string | number)[][] = [
    [
      'Tanggal',
      'Invoice',
      'Customer',
      'Kasir',
      'Items',
      'Payment',
      'Tipe',
      'Sisa piutang (IDR)',
      'Jatuh tempo',
      'Total (IDR)',
    ],
    ...transactions
      .slice()
      .sort((a, b) => b.createdAt - a.createdAt)
      .map((t) => {
        const isTempo = t.paymentTerms === 'tempo';
        const dueText =
          t.dueDate && isTempo
            ? new Date(t.dueDate).toLocaleDateString('id-ID', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : '';
        return [
          new Date(t.createdAt).toLocaleString('id-ID', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
          t.invoiceNumber,
          t.customerName ?? 'Walk-in',
          t.cashierName ?? '—',
          t.itemCount,
          t.paymentMethod,
          isTempo ? 'TEMPO' : 'LUNAS',
          reportableOutstanding(t),
          dueText,
          t.grandTotal,
        ];
      }),
  ];

  const wb = XLSX.utils.book_new();

  const writeSheet = (name: string, rows: (string | number)[][]) => {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    // Set column widths based on content length
    const colWidths = rows.reduce<number[]>((widths, row) => {
      row.forEach((cell, i) => {
        const len = String(cell ?? '').length + 2;
        widths[i] = Math.max(widths[i] ?? 0, len);
      });
      return widths;
    }, []);
    ws['!cols'] = colWidths.map((w) => ({ wch: Math.min(Math.max(w, 10), 50) }));
    // Freeze the first row (header) so scrolling through the long
    // Transactions list keeps the column titles visible.
    if (rows.length > 1) ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, name);
  };

  writeSheet('Summary', summaryRows);
  writeSheet('Payment Breakdown', paymentRows);
  writeSheet('Top Products', productsRows);
  writeSheet('Cashiers', cashiersRows);
  if (transactions.length > 0) {
    writeSheet('Transactions', txRows);
  }

  const arrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([arrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

// ---------------------------------------------------------------------------
// PDF export
// ---------------------------------------------------------------------------
// Beautifully styled PDF report. Uses jspdf programmatically with proper
// layout, color, and hierarchy so the output looks like a real report
// you'd hand to an accountant — not a raw text dump.
//
// Layout (A4 portrait):
//   ┌─────────────────────────────────────────────┐
//   │  [LOGO]  COMPANY NAME                       │  ← branded header
//   │          address · phone · email           │
//   ├─────────────────────────────────────────────┤
//   │  LAPORAN PENJUALAN                          │  ← cover title
//   │  Event Name (CODE) · Periode                │
//   ├─────────────────────────────────────────────┤
//   │  ┌─KPI─┐ ┌─KPI─┐ ┌─KPI─┐ ┌─KPI─┐            │  ← KPI cards row
//   │  └─────┘ └─────┘ └─────┘ └─────┘            │
//   ├─────────────────────────────────────────────┤
//   │  ▍ Section Title                            │  ← accent bar
//   │  table...                                   │  ← styled table
//   ├─────────────────────────────────────────────┤
//   │  company · address     Halaman X dari Y     │  ← footer
//   └─────────────────────────────────────────────┘
//
// Lazy-loads jspdf so the ~360KB library doesn't sit in the main bundle.

export interface PdfCompanyInfo {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  logoUrl?: string;
}

export interface PdfTransaction {
  invoiceNumber: string;
  createdAt: number;
  customerName?: string;
  cashierName?: string;
  paymentMethod: string;
  paymentStatus: string;
  itemCount: number;
  grandTotal: number;
  /** Outstanding (sisa piutang). When 0 the row is treated as fully paid. */
  outstandingAmount?: number;
  /** Due date (epoch ms). Only meaningful for tempo transactions. */
  dueDate?: number;
  /** 'lunas' | 'tempo'. Defaults to 'lunas' for legacy data. */
  paymentTerms?: 'lunas' | 'tempo';
}

export async function buildEventReportPdf(
  event: EventReportEventMeta,
  summary: EventSummary,
  company: PdfCompanyInfo = { name: 'OPHTH POS' },
  transactions: PdfTransaction[] = [],
): Promise<Blob> {
  const { jsPDF } = await loadJsPdf();
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  // ── Color palette (RGB) ────────────────────────────────────────────
  const C = {
    primary: [14, 116, 144] as [number, number, number],     // teal
    primaryDark: [12, 90, 112] as [number, number, number],
    accent: [217, 119, 6] as [number, number, number],      // amber
    success: [22, 163, 74] as [number, number, number],     // green
    danger: [220, 38, 38] as [number, number, number],      // red
    warning: [202, 138, 4] as [number, number, number],     // amber-darker
    ink: [17, 24, 39] as [number, number, number],          // near-black
    muted: [107, 114, 128] as [number, number, number],     // gray
    border: [226, 232, 240] as [number, number, number],    // light slate
    cardBg: [248, 250, 252] as [number, number, number],    // very light slate
    band: [241, 245, 249] as [number, number, number],      // light slate
    rowAlt: [252, 252, 253] as [number, number, number],     // zebra
  };

  // ── Layout constants ──────────────────────────────────────────────
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const M = 40;                       // outer margin
  const CONTENT_W = PAGE_W - M * 2;
  const FOOTER_H = 30;
  const HEADER_H = 44;

  // y is the current cursor (top of next line). We reset to M at the
  // top of each page and grow downward.
  let y = M;

  // ── Helpers ────────────────────────────────────────────────────────
  const ensureSpace = (h: number) => {
    if (y + h > PAGE_H - M - FOOTER_H) {
      doc.addPage();
      y = M;
      drawHeaderBar();
    }
  };

  // Set both fill and stroke colour at once
  const setRgb = (c: [number, number, number]) => {
    doc.setTextColor(c[0], c[1], c[2]);
    doc.setDrawColor(c[0], c[1], c[2]);
  };

  // Truncate a string to fit a max character count, with ellipsis.
  const fit = (s: string, max: number): string =>
    s.length > max ? s.slice(0, max - 1) + '…' : s;

  // Format a number with Indonesian locale
  const fmtIdr = (n: number) => 'Rp ' + n.toLocaleString('id-ID');
  const fmtNum = (n: number) => n.toLocaleString('id-ID');

  // Draw the colored brand band at the very top of every page.
  const drawTopBand = () => {
    doc.setFillColor(...C.primary);
    doc.rect(0, 0, PAGE_W, 6, 'F');
  };

  // Header bar with company name + address. Called at the top of each
  // page (right after the colored band). Tall enough to fit the company
  // name, contact line, AND any long company names without overflow.
  const drawHeaderBar = () => {
    drawTopBand();
    y = M;
    doc.setFillColor(...C.cardBg);
    doc.rect(M, y - 4, CONTENT_W, HEADER_H - 6, 'F');

    // Logo placeholder — a 32pt square with the first letter of the
    // company name, white on teal. We size the letter to fit.
    const LOGO_SIZE = 32;
    const LOGO_X = M + 6;
    const LOGO_Y = y - 0;
    doc.setFillColor(...C.primary);
    doc.roundedRect(LOGO_X, LOGO_Y, LOGO_SIZE, LOGO_SIZE, 5, 5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    const initial = (company.name || 'O').charAt(0).toUpperCase();
    doc.text(initial, LOGO_X + LOGO_SIZE / 2, LOGO_Y + LOGO_SIZE / 2 + 5.5, {
      align: 'center',
    });

    // Company name + contact — start AFTER the logo + padding
    const textX = LOGO_X + LOGO_SIZE + 12;
    setRgb(C.ink);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(fit(company.name, 40), textX, y + 14);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setRgb(C.muted);
    const contactParts = [company.address, company.phone, company.email]
      .filter(Boolean) as string[];
    if (contactParts.length > 0) {
      doc.text(fit(contactParts.join(' · '), 90), textX, y + 28);
    }

    y = M + HEADER_H; // below the header bar
  };

  // Footer with company line on the left, page number on the right.
  // Drawn on every page; called at the end after the report body.
  const drawFooters = () => {
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      const fy = PAGE_H - M + 10;
      // Thin divider line
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.5);
      doc.line(M, fy - 14, M + CONTENT_W, fy - 14);
      // Left: company line
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      setRgb(C.muted);
      const leftText = fit(
        company.name + (company.address ? ' · ' + company.address.split('\n')[0] : ''),
        80,
      );
      doc.text(leftText, M, fy);
      // Right: page number
      doc.text(`Halaman ${i} dari ${pageCount}`, M + CONTENT_W, fy, {
        align: 'right',
      });
    }
  };

  // Section title with a 4pt accent bar on the left. Slightly more
  // breathing room between the bar and the title.
  const sectionTitle = (text: string, sub?: string) => {
    ensureSpace(40);
    y += 4;
    doc.setFillColor(...C.accent);
    doc.rect(M, y - 8, 4, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    setRgb(C.ink);
    doc.text(text, M + 12, y + 4);
    if (sub) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      setRgb(C.muted);
      doc.text(sub, M + 12, y + 18);
      y += 10;
    }
    y += 18;
  };

  // KPI card — small box with label + big number. Bigger now (84pt
  // tall) so the big number reads well.
  const kpiCard = (
    x: number,
    y0: number,
    w: number,
    h: number,
    label: string,
    value: string,
    sub?: string,
    color: [number, number, number] = C.primary,
  ) => {
    doc.setFillColor(...C.cardBg);
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.5);
    doc.roundedRect(x, y0, w, h, 8, 8, 'FD');

    // Top accent stripe
    doc.setFillColor(...color);
    doc.rect(x, y0, w, 5, 'F');

    // Label
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setRgb(C.muted);
    doc.text(label.toUpperCase(), x + 12, y0 + 24);

    // Big number
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    setRgb(C.ink);
    // Slightly smaller font for long values like "Rp 1.500.000"
    doc.text(value, x + 12, y0 + 50);

    // Sub label
    if (sub) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      setRgb(C.muted);
      doc.text(sub, x + 12, y0 + h - 10);
    }
  };

  // Styled table — header row with banded fill, alternating body rows.
  // Increased row height (18 → 22) and font (9 → 10) for readability.
  type Column = {
    key: string;
    label: string;
    align?: 'left' | 'right' | 'center';
    width: number; // 0..1, sum should be ~1
    format?: (v: unknown) => string;
  };
  const styledTable = (columns: Column[], rows: Array<Record<string, unknown>>) => {
    if (rows.length === 0) return;
    const colX = columns.reduce<{ x: number[]; w: number[] }>(
      (acc, col) => {
        const w = CONTENT_W * col.width;
        acc.x.push(acc.x.length === 0 ? M : acc.x[acc.x.length - 1] + acc.w[acc.w.length - 1]);
        acc.w.push(w);
        return acc;
      },
      { x: [], w: [] },
    );
    const ROW_H = 24;          // ↑ from 18 — easier to read
    const HEADER_H = 26;      // ↑ from 22

    // Header row
    ensureSpace(HEADER_H + ROW_H);
    doc.setFillColor(...C.band);
    doc.rect(M, y - 4, CONTENT_W, HEADER_H, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);         // ↑ from 8
    setRgb(C.ink);
    columns.forEach((col, i) => {
      const tx = colX.x[i];
      const tw = colX.w[i];
      const align = col.align ?? 'left';
      const padX = 8;
      const textX =
        align === 'right' ? tx + tw - padX : align === 'center' ? tx + tw / 2 : tx + padX;
      doc.text(col.label.toUpperCase(), textX, y + 11, { align });
    });
    y += HEADER_H;

    // Body rows with alternating fill
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);        // ↑ from 9
    rows.forEach((row, idx) => {
      ensureSpace(ROW_H);
      if (idx % 2 === 0) {
        doc.setFillColor(...C.rowAlt);
        doc.rect(M, y - 4, CONTENT_W, ROW_H, 'F');
      }
      setRgb(C.ink);
      columns.forEach((col, i) => {
        const tx = colX.x[i];
        const tw = colX.w[i];
        const align = col.align ?? 'left';
        const padX = 8;
        const textX =
          align === 'right' ? tx + tw - padX : align === 'center' ? tx + tw / 2 : tx + padX;
        const raw = row[col.key];
        const text =
          col.format && raw !== undefined && raw !== null
            ? col.format(raw)
            : raw === undefined || raw === null
              ? '—'
              : String(raw);
        // Truncate long left-aligned text
        const maxChars = Math.max(6, Math.floor(tw / 5));
        const finalText =
          align === 'left' && text.length > maxChars
            ? text.slice(0, maxChars - 1) + '…'
            : text;
        doc.text(finalText, textX, y + 12, { align });
      });
      y += ROW_H;
    });

    y += 8;
  };

  // ════════════════════════════════════════════════════════════════════
  // Render
  // ════════════════════════════════════════════════════════════════════
  drawHeaderBar();
  y += 6;

  // Cover title
  ensureSpace(80);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  setRgb(C.primary);
  doc.text('Laporan Penjualan', M, y + 22);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  setRgb(C.muted);
  doc.text('Per-event sales report', M, y + 40);
  y += 54;

  // Event metadata block
  ensureSpace(60);
  doc.setFillColor(...C.cardBg);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.5);
  doc.roundedRect(M, y, CONTENT_W, 50, 4, 4, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  setRgb(C.ink);
  doc.text(event.name, M + 12, y + 18);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setRgb(C.muted);
  doc.text(`Kode event: ${event.code}`, M + 12, y + 33);
  if (event.startAt) {
    const start = new Date(event.startAt).toLocaleString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    const end = event.endAt
      ? new Date(event.endAt).toLocaleString('id-ID', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'masih aktif';
    doc.text(`Periode: ${start} → ${end}`, M + 12, y + 44);
  }
  y += 64;

  // KPI cards row — 4 cards
  ensureSpace(90);
  const kpiW = (CONTENT_W - 12) / 4;
  const kpiH = 82;
  const kpiY = y;
  kpiCard(M + 0 * (kpiW + 4), kpiY, kpiW, kpiH, 'Total Penjualan', fmtIdr(summary.totalSales), `${fmtNum(summary.totalTransactions)} transaksi`, C.primary);
  kpiCard(M + 1 * (kpiW + 4), kpiY, kpiW, kpiH, 'Item Terjual', fmtNum(summary.totalItems), 'unit', C.accent);
  kpiCard(M + 2 * (kpiW + 4), kpiY, kpiW, kpiH, 'Avg. Transaksi', fmtIdr(Math.round(summary.averageTransactionValue)), 'per transaksi', C.success);
  kpiCard(M + 3 * (kpiW + 4), kpiY, kpiW, kpiH, 'Retur', fmtIdr(summary.returnsTotal), `${fmtNum(summary.returnsCount)} invoice`, summary.returnsTotal > 0 ? C.danger : C.muted);
  y += kpiH + 18;

  // ── Detail: Payment Breakdown ──
  if (summary.paymentMethodBreakdown.length > 0) {
    sectionTitle('Payment Breakdown');
    styledTable(
      [
        { key: 'method', label: 'Metode', width: 0.5 },
        { key: 'count', label: 'Jumlah', align: 'right', width: 0.2, format: (v) => fmtNum(Number(v)) },
        { key: 'total', label: 'Total (IDR)', align: 'right', width: 0.3, format: (v) => fmtIdr(Number(v)) },
      ],
      summary.paymentMethodBreakdown.map((p) => ({
        method: p.method.toUpperCase(),
        count: p.count,
        total: p.total,
      })),
    );
  }

  // ── Detail: Top Products ──
  if (summary.topProducts.length > 0) {
    sectionTitle('Top Products');
    styledTable(
      [
        { key: 'rank', label: '#', align: 'center', width: 0.05 },
        { key: 'name', label: 'Produk', width: 0.5 },
        { key: 'qty', label: 'Qty', align: 'right', width: 0.15, format: (v) => fmtNum(Number(v)) },
        { key: 'revenue', label: 'Revenue (IDR)', align: 'right', width: 0.3, format: (v) => fmtIdr(Number(v)) },
      ],
      summary.topProducts.map((p, i) => ({
        rank: i + 1,
        name: p.name,
        qty: p.qty,
        revenue: p.revenue,
      })),
    );
  }

  // ── Detail: Cashier Breakdown ──
  if (summary.cashierBreakdown.length > 0) {
    sectionTitle('Cashier Breakdown');
    styledTable(
      [
        { key: 'cashierName', label: 'Kasir', width: 0.45 },
        { key: 'count', label: 'Transaksi', align: 'right', width: 0.2, format: (v) => fmtNum(Number(v)) },
        { key: 'total', label: 'Total (IDR)', align: 'right', width: 0.35, format: (v) => fmtIdr(Number(v)) },
      ],
      summary.cashierBreakdown.map((c) => ({
        cashierName: c.cashierName,
        count: c.count,
        total: c.total,
      })),
    );
  }

  // ── Detail: Discount & Tax summary (if any) ──
  if (summary.totalDiscount > 0 || summary.totalTax > 0) {
    sectionTitle('Discount & Tax');
    styledTable(
      [
        { key: 'metric', label: 'Metrik', width: 0.6 },
        { key: 'amount', label: 'Jumlah (IDR)', align: 'right', width: 0.4, format: (v) => fmtIdr(Number(v)) },
      ],
      [
        ...(summary.totalDiscount > 0
          ? [{ metric: 'Total diskon diberikan', amount: summary.totalDiscount }]
          : []),
        ...(summary.totalTax > 0
          ? [{ metric: `Total PPN`, amount: summary.totalTax }]
          : []),
        { metric: 'Net (setelah retur)', amount: summary.totalSales - summary.returnsTotal },
      ],
    );
  }

  // ── Detail: Daftar Transaksi ── (full transaction list)
  if (transactions.length > 0) {
    sectionTitle(
      'Daftar Transaksi',
      `${fmtNum(transactions.length)} transaksi · diurut dari yang terbaru`,
    );

    const txCols: Column[] = [
      { key: 'createdAt', label: 'Waktu', width: 0.11, format: (v) => formatTxTime(Number(v)) },
      { key: 'invoiceNumber', label: 'Invoice', width: 0.12 },
      { key: 'customerName', label: 'Customer', width: 0.16 },
      { key: 'itemCount', label: 'Item', align: 'right', width: 0.06, format: (v) => fmtNum(Number(v)) },
      { key: 'paymentMethod', label: 'Method', width: 0.08 },
      { key: 'terms', label: 'Tempo?', align: 'center', width: 0.07 },
      { key: 'outstanding', label: 'Sisa piutang', align: 'right', width: 0.14, format: (v) => fmtIdr(Number(v)) },
      { key: 'grandTotal', label: 'Total (IDR)', align: 'right', width: 0.13, format: (v) => fmtIdr(Number(v)) },
    ];
    const txRows: Array<Record<string, unknown>> = transactions
      .slice()
      .sort((a, b) => b.createdAt - a.createdAt)
      .map((t) => {
        const isTempo = t.paymentTerms === 'tempo';
        return {
          createdAt: t.createdAt,
          invoiceNumber: t.invoiceNumber,
          customerName: t.customerName || 'Walk-in',
          itemCount: t.itemCount,
          paymentMethod: (t.paymentMethod || 'cash').toUpperCase(),
          terms: isTempo ? 'TEMPO' : 'LUNAS',
          outstanding: reportableOutstanding(t),
          grandTotal: t.grandTotal,
        };
      });

    // Use a slightly tighter row for transaction lists (lots of rows).
    // Same helper, but with smaller row height.
    const ROW_H_TX = 20;
    const HEADER_H = 26;

    // Same column layout
    const colX = txCols.reduce<{ x: number[]; w: number[] }>(
      (acc, col) => {
        const w = CONTENT_W * col.width;
        acc.x.push(acc.x.length === 0 ? M : acc.x[acc.x.length - 1] + acc.w[acc.w.length - 1]);
        acc.w.push(w);
        return acc;
      },
      { x: [], w: [] },
    );

    // Header
    ensureSpace(HEADER_H + ROW_H_TX);
    doc.setFillColor(...C.band);
    doc.rect(M, y - 4, CONTENT_W, HEADER_H, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    setRgb(C.ink);
    txCols.forEach((col, i) => {
      const tx = colX.x[i];
      const tw = colX.w[i];
      const align = col.align ?? 'left';
      const padX = 6;
      const textX =
        align === 'right' ? tx + tw - padX : align === 'center' ? tx + tw / 2 : tx + padX;
      doc.text(col.label.toUpperCase(), textX, y + 11, { align });
    });
    y += HEADER_H;

    // Body
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    txRows.forEach((row, idx) => {
      ensureSpace(ROW_H_TX);
      if (idx % 2 === 0) {
        doc.setFillColor(...C.rowAlt);
        doc.rect(M, y - 4, CONTENT_W, ROW_H_TX, 'F');
      }
      setRgb(C.ink);
      txCols.forEach((col, i) => {
        const tx = colX.x[i];
        const tw = colX.w[i];
        const align = col.align ?? 'left';
        const padX = 6;
        const textX =
          align === 'right' ? tx + tw - padX : align === 'center' ? tx + tw / 2 : tx + padX;
        const raw = row[col.key];
        const text =
          col.format && raw !== undefined && raw !== null
            ? col.format(raw)
            : raw === undefined || raw === null
              ? '—'
              : String(raw);
        const maxChars = Math.max(6, Math.floor(tw / 4.5));
        const finalText =
          align === 'left' && text.length > maxChars
            ? text.slice(0, maxChars - 1) + '…'
            : text;
        doc.text(finalText, textX, y + 10, { align });
      });
      y += ROW_H_TX;
    });
    y += 8;
  }

  // Footer on every page
  drawFooters();

  return doc.output('blob');
}

function formatTxTime(ms: number): string {
  if (!ms) return '—';
  const d = new Date(ms);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
}
